import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Chip, alpha, Button,
} from '@mui/material';
import {
  Warning, AssignmentInd, Payments, Groups, Map, QueuePlayNext,
} from '@mui/icons-material';
import type { DashboardOverview } from './DashboardStatsGrid';
import { aggregateJobStats, formatInProgressAlertLabel, formatNeedsAssignmentLabel, formatActiveHint, formatPendingHint } from '../../utils/jobStats';
import { DASHBOARD_NAV } from '../../config/dashboardNavigation';

interface DashboardOpsAlertsProps {
  overview?: DashboardOverview;
  enabledModules?: string[];
  isSuperAdmin?: boolean;
  role?: string;
  embedded?: boolean;
}

export default function DashboardOpsAlerts({
  overview = {},
  enabledModules = [],
  isSuperAdmin,
  role,
  embedded,
}: DashboardOpsAlertsProps) {

  const jobStats = useMemo(
    () => aggregateJobStats(overview, enabledModules, { isSuperAdmin, role }),
    [overview, enabledModules, isSuperAdmin, role],
  );

  const alerts = [
    jobStats.pendingJobs > 0 && {
      label: formatNeedsAssignmentLabel(jobStats.pendingJobs),
      hint: formatPendingHint(jobStats.pendingJobs, jobStats.pendingByModule),
      icon: <QueuePlayNext fontSize="small" />,
      color: '#dc2626',
      action: 'Open Dispatcher',
      path: DASHBOARD_NAV.needsAction,
    },
    jobStats.activeJobs > 0 && {
      label: formatInProgressAlertLabel(jobStats.activeJobs),
      hint: formatActiveHint(jobStats.activeJobs, jobStats.activeByModule),
      icon: <AssignmentInd fontSize="small" />,
      color: '#0891b2',
      action: 'View Dispatcher',
      path: DASHBOARD_NAV.activeJobs,
    },
    (overview.pendingSettlements ?? 0) > 0 && {
      label: `${overview.pendingSettlements} pending settlements`,
      hint: 'Partner payouts awaiting processing',
      icon: <Payments fontSize="small" />,
      color: '#ca8a04',
      action: 'Settlements',
      path: DASHBOARD_NAV.pendingSettlements,
    },
    (overview.pendingPartnerApprovals ?? 0) > 0 && {
      label: `${overview.pendingPartnerApprovals} partners pending approval`,
      hint: 'Documents or onboarding review needed',
      icon: <Groups fontSize="small" />,
      color: '#9333ea',
      action: 'Partners',
      path: DASHBOARD_NAV.partnerApprovals,
    },
    (overview.totalZones ?? 0) === 0 && {
      label: 'No zones configured',
      hint: 'Set up coverage & pricing before going live',
      icon: <Map fontSize="small" />,
      color: '#64748b',
      action: 'Add Zone',
      path: DASHBOARD_NAV.zones,
    },
  ].filter(Boolean) as {
    label: string; hint: string; icon: React.ReactNode; color: string; action: string; path: string;
  }[];

  if (!alerts.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: embedded ? 0 : 3,
          borderRadius: 3,
          border: embedded ? 0 : 1,
          borderColor: 'divider',
          bgcolor: alpha('#059669', 0.04),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ color: '#059669' }}><Warning fontSize="small" /></Box>
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            All clear — no urgent operational alerts right now.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box mb={embedded ? 0 : 3}>
      {!embedded && (
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Action items</Typography>
      )}
      <Stack spacing={1}>
        {alerts.map((alert) => (
          <Paper
            key={alert.label}
            elevation={0}
            component={RouterLink}
            to={alert.path}
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              border: 1,
              borderColor: alpha(alert.color, 0.25),
              bgcolor: alpha(alert.color, 0.04),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
              <Box sx={{ color: alert.color, display: 'flex' }}>{alert.icon}</Box>
              <Box minWidth={0}>
                <Typography variant="body2" fontWeight={700}>{alert.label}</Typography>
                <Typography variant="caption" color="text.secondary">{alert.hint}</Typography>
              </Box>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              component="span"
              sx={{ borderColor: alpha(alert.color, 0.4), color: alert.color, fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              {alert.action}
            </Button>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
